import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useJsApiLoader, GoogleMap, Marker, DirectionsRenderer } from '@react-google-maps/api';

/**
 * Centered map used by the ride app:
 *  - shows the route from pickup → dropoff when `route` is set
 *  - centers a marker at `driver` location while tracking
 */
const RideMap = ({
  route = null,         // { origin, destination } addresses
  pickup = null,        // { label, lat, lng }
  dropoff = null,
  driver = null,        // { latitude, longitude } driver live pos
  center,
  zoom = 14,
  fitRoute = true,
  className = '',
}) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  });

  const mapRef = useRef(null);
  const [directions, setDirections] = useState(null);
  const serviceRef = useRef(null);

  const showRoute = !!(route?.origin && route?.destination && isLoaded);

  useEffect(() => {
    if (!isLoaded) return;
    serviceRef.current = new window.google.maps.DirectionsService();
  }, [isLoaded]);

  useEffect(() => {
    if (!showRoute || !serviceRef.current) return;
    serviceRef.current.route(
      {
        origin: route.origin,
        destination: route.destination,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (results, status) => {
        if (status === window.google.maps.DirectionsStatus.OK && results) {
          setDirections(results);
        } else {
          setDirections(null);
        }
      }
    );
  }, [showRoute, route]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  useEffect(() => {
    if (!fitRoute || !mapRef.current || !directions || !window.google) return;
    const bounds = new window.google.maps.LatLngBounds();
    directions.routes[0].overview_path.forEach((p) => bounds.extend(p));
    mapRef.current.fitBounds(bounds, 60);
  }, [directions, fitRoute]);

  if (!isLoaded) {
    return <div className={`ride-map-skeleton ${className}`} data-testid="map-skeleton" />;
  }

  const driverPos = driver?.latitude ? { lat: driver.latitude, lng: driver.longitude } : null;

  return (
    <GoogleMap
      className={className}
      center={center || (pickup && dropoff ? { lat: pickup.lat, lng: pickup.lng } : { lat: 24.8607, lng: 67.0011 })}
      zoom={zoom}
      mapContainerStyle={{ width: '100%', height: '100%' }}
      options={{
        zoomControl: true,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
      }}
      onLoad={onMapLoad}
    >
      {directions && <DirectionsRenderer directions={directions} options={{ preserveViewport: true }} />}

      {pickup && (
        <Marker
          position={{ lat: pickup.lat, lng: pickup.lng }}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 9,
            fillColor: '#16a34a',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2.5,
          }}
        />
      )}
      {dropoff && (
        <Marker
          position={{ lat: dropoff.lat, lng: dropoff.lng }}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 9,
            fillColor: '#dc2626',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2.5,
          }}
        />
      )}
      {driverPos && (
        <Marker
          position={driverPos}
          icon={{
            url: 'http://maps.google.com/mapfiles/ms/icons/truck.png',
            scaledSize: new window.google.maps.Size(40, 40),
          }}
          animation={window.google.maps.Animation.DROP}
        />
      )}
    </GoogleMap>
  );
};

export default React.memo(RideMap);
